/**
 * Strings for the system layer: auth, sync status, account, toasts, dialogs.
 * Spread into the main catalog (translations.ts). `SYSTEM_EN` is typed against
 * `SYSTEM_ES`, so a missing English string fails to compile.
 */

export const SYSTEM_ES = {
  // ── Auth callback ───────────────────────────────────────────────────────
  'auth.callback.denied': 'Cancelaste el acceso en Google. Sin él, tus datos se quedan solo en este dispositivo.',
  'auth.callback.stateMismatch': 'La respuesta de Google no corresponde a esta pestaña. Vuelve a intentarlo desde la app.',
  'auth.callback.generic': 'No se pudo completar la conexión con Google.',
  'auth.callback.retry': 'Intentar de nuevo',
  'auth.callback.continueOffline': 'Seguir sin conexión',

  // ── Sync status (chip + settings) ───────────────────────────────────────
  'sync.status.synced': 'Sincronizado',
  'sync.chipLabel': 'Sincronización: {status}',
  'sync.status.syncing': 'Sincronizando…',
  'sync.status.pending': 'Cambios por subir',
  'sync.status.offline': 'Sin conexión',
  'sync.status.needs_auth': 'Reconectar Google',
  'sync.status.needs_consent': 'Falta permiso de Drive',
  'sync.status.signed_out': 'Sin sesión',
  'sync.status.error': 'Error al sincronizar',
  'sync.detail.synced': 'Todo lo de este dispositivo está en tu Drive.',
  'sync.detail.syncing': 'Comparando con tu Drive…',
  'sync.detail.pending': 'Guardado en este dispositivo. Se subirá a Drive en la próxima sincronización.',
  'sync.detail.offline': 'Puedes seguir estudiando y escribiendo. Se sincroniza al volver la conexión.',
  'sync.detail.needs_auth': 'La sesión de Google caducó. Tus datos siguen aquí; reconecta para sincronizar.',
  'sync.detail.needs_consent': 'Google no concedió acceso a Drive. Sin ese permiso no se puede guardar en tu Drive.',
  'sync.detail.signed_out': 'Inicia sesión para guardar una copia en tu Google Drive.',
  'sync.detail.error': 'Se reintentará automáticamente.',
  'sync.lastSynced': 'Última sincronización: {when}',
  'sync.never': 'Aún no se ha sincronizado',
  'sync.pendingOps': '{count} cambios en cola',
  'sync.parkedOps': '{count} cambios no se pudieron subir',
  'sync.action.syncNow': 'Sincronizar ahora',
  'sync.action.reconnect': 'Reconectar',
  'sync.action.grant': 'Conceder acceso a Drive',
  'sync.action.signIn': 'Iniciar sesión',
  'sync.action.retry': 'Reintentar',
  'sync.toast.done': 'Sincronizado con Drive',
  'sync.toast.failed': 'No se pudo sincronizar: {error}',

  // ── Navigation ──────────────────────────────────────────────────────────
  'nav.main': 'Navegación principal',
  'nav.search': 'Buscar en todo',

  // ── Account ─────────────────────────────────────────────────────────────
  'account.mismatch.title': 'Otra cuenta de Google',
  'account.mismatch.body': 'Este dispositivo guarda los datos de {owner}. Acabas de entrar como {other}.',
  'account.mismatch.switch': 'Usar {other} (borra los datos locales de {owner})',
  'account.mismatch.back': 'Volver a {owner}',
  'account.signOut': 'Cerrar sesión',
  'account.signOut.confirmTitle': '¿Cerrar sesión?',
  'account.signOut.confirmBody': 'Tus datos siguen en tu Google Drive. Puedes mantener la copia de este dispositivo para seguir usándola sin conexión, o borrarla.',
  'account.signOut.keep': 'Cerrar sesión',
  'account.signOut.wipe': 'Cerrar sesión y borrar este dispositivo',
  'account.wiping': 'Borrando los datos de este dispositivo…',

  // ── Generic UI ──────────────────────────────────────────────────────────
  'conflict.noteCopy': '«{title}» se editó en otro dispositivo a la vez. Tu versión se guardó como copia.',
  'conflict.openNote': 'Abrir nota',
  'conflict.openCopy': 'Abrir mi copia',

  // ── Confirmations and feedback that used window.confirm / alert ─────────
  'deckEditor.deleteCard.title': '¿Eliminar esta tarjeta?',
  'deckEditor.deleteCards.title': '¿Eliminar {count} tarjetas?',
  'deckEditor.delete.body': 'También se borra su historial de repaso. No se puede deshacer.',
  'notes.promote.title': '¿Convertir «{name}» en Curso Largo?',
  'notes.promote.body': 'Se creará una carpeta con este nombre en Drive. Esta nota pasa a ser el primer módulo del curso (conserva su título y su contenido) y se crea un índice al que podrás añadir más módulos. No se pierde nada y los enlaces a esta nota siguen funcionando. No se puede deshacer desde la app.',
  'notes.promote.confirm': 'Convertir en Curso Largo',
  'notes.promote.fallbackName': 'esta nota',
  'appearance.fileTooLarge': 'El archivo es demasiado grande (máximo {max} MB).',
  'appearance.uploadFailed': 'No se pudo subir la imagen a Google Drive.',
  'common.delete': 'Eliminar',

  // ── Notes save indicator ────────────────────────────────────────────────
  'notes.save.saving': 'Guardando…',
  'notes.save.local': 'Guardado en este dispositivo',
  'notes.save.drive': 'Guardado en Drive',
  'notes.loading': 'Cargando notas…',
  'importDeck.notReady': 'La base de datos local aún no está lista. Inténtalo en unos segundos.',
  'deckEditor.saved': 'Guardado en este dispositivo · se subirá a Drive al sincronizar',
  'deckEditor.loading': 'Cargando tarjetas…',
  'session.preparing': 'Preparando la sesión…',
  'notes.firstRun.title': 'Todavía no tienes notas',
  'notes.firstRun.body': 'Escribe en Markdown, enlaza notas con [[corchetes]] y convierte apartados en tarjetas de repaso. Se guardan en este dispositivo y en tu Drive al sincronizar.',

  'common.confirm': 'Confirmar',
  'common.dismiss': 'Descartar',
  'common.undo': 'Deshacer',
  'common.retry': 'Reintentar',
  'common.loading': 'Cargando…',
} as const

export const SYSTEM_EN: Record<keyof typeof SYSTEM_ES, string> = {
  'auth.callback.denied': 'You cancelled access in Google. Without it, your data stays on this device only.',
  'auth.callback.stateMismatch': "Google's response doesn't match this tab. Try again from the app.",
  'auth.callback.generic': 'The connection with Google could not be completed.',
  'auth.callback.retry': 'Try again',
  'auth.callback.continueOffline': 'Continue offline',

  'sync.status.synced': 'Synced',
  'sync.chipLabel': 'Sync: {status}',
  'sync.status.syncing': 'Syncing…',
  'sync.status.pending': 'Changes to upload',
  'sync.status.offline': 'Offline',
  'sync.status.needs_auth': 'Reconnect Google',
  'sync.status.needs_consent': 'Drive permission missing',
  'sync.status.signed_out': 'Signed out',
  'sync.status.error': 'Sync error',
  'sync.detail.synced': 'Everything on this device is in your Drive.',
  'sync.detail.syncing': 'Comparing with your Drive…',
  'sync.detail.pending': 'Saved on this device. It will upload to Drive on the next sync.',
  'sync.detail.offline': 'You can keep studying and writing. It syncs when the connection is back.',
  'sync.detail.needs_auth': 'Your Google session expired. Your data is still here; reconnect to sync.',
  'sync.detail.needs_consent': "Google didn't grant Drive access. Without it nothing can be saved to your Drive.",
  'sync.detail.signed_out': 'Sign in to keep a copy in your Google Drive.',
  'sync.detail.error': 'It will retry automatically.',
  'sync.lastSynced': 'Last sync: {when}',
  'sync.never': 'Not synced yet',
  'sync.pendingOps': '{count} changes queued',
  'sync.parkedOps': '{count} changes could not be uploaded',
  'sync.action.syncNow': 'Sync now',
  'sync.action.reconnect': 'Reconnect',
  'sync.action.grant': 'Grant Drive access',
  'sync.action.signIn': 'Sign in',
  'sync.action.retry': 'Retry',
  'sync.toast.done': 'Synced with Drive',
  'sync.toast.failed': 'Could not sync: {error}',

  'nav.main': 'Main navigation',
  'nav.search': 'Search everything',

  'account.mismatch.title': 'A different Google account',
  'account.mismatch.body': 'This device holds the data of {owner}. You just signed in as {other}.',
  'account.mismatch.switch': 'Use {other} (erases the local data of {owner})',
  'account.mismatch.back': 'Back to {owner}',
  'account.signOut': 'Sign out',
  'account.signOut.confirmTitle': 'Sign out?',
  'account.signOut.confirmBody': 'Your data stays in your Google Drive. You can keep this device’s copy to use it offline, or erase it.',
  'account.signOut.keep': 'Sign out',
  'account.signOut.wipe': 'Sign out and erase this device',
  'account.wiping': 'Erasing this device’s data…',

  'conflict.noteCopy': '“{title}” was edited on another device at the same time. Your version was saved as a copy.',
  'conflict.openNote': 'Open note',
  'conflict.openCopy': 'Open my copy',

  'deckEditor.deleteCard.title': 'Delete this card?',
  'deckEditor.deleteCards.title': 'Delete {count} cards?',
  'deckEditor.delete.body': 'Its review history is deleted too. This cannot be undone.',
  'notes.promote.title': 'Turn “{name}” into a Long Course?',
  'notes.promote.body': 'A folder with this name will be created in Drive. This note becomes the first module of the course (it keeps its title and content) and an index is created where you can add more modules. Nothing is lost and links to this note keep working. This cannot be undone from the app.',
  'notes.promote.confirm': 'Turn into Long Course',
  'notes.promote.fallbackName': 'this note',
  'appearance.fileTooLarge': 'The file is too large (max {max} MB).',
  'appearance.uploadFailed': 'The image could not be uploaded to Google Drive.',
  'common.delete': 'Delete',

  'notes.save.saving': 'Saving…',
  'notes.save.local': 'Saved on this device',
  'notes.save.drive': 'Saved to Drive',
  'notes.loading': 'Loading notes…',
  'importDeck.notReady': 'The local database is not ready yet. Try again in a few seconds.',
  'deckEditor.saved': 'Saved on this device · uploads to Drive on the next sync',
  'deckEditor.loading': 'Loading cards…',
  'session.preparing': 'Preparing the session…',
  'notes.firstRun.title': 'No notes yet',
  'notes.firstRun.body': 'Write in Markdown, link notes with [[brackets]] and turn sections into review cards. They are saved on this device and to your Drive when it syncs.',

  'common.confirm': 'Confirm',
  'common.dismiss': 'Dismiss',
  'common.undo': 'Undo',
  'common.retry': 'Retry',
  'common.loading': 'Loading…',
}
